import type { BVFormData } from '@/components/Templates/BenefitVerification/BenefitVerificationForm/BenefitVerification';
import createHttp from '@/config/http';
import type {
	BenefitVerification,
	BenefitVerificationPayload,
	BVRecord,
	BVResponse,
	IVRFileData,
	PRDocumentData,
} from '@/types/benefitsVerification';
import type { BvProduct, UpdateBVProductPayload } from '@/types/product';
import type { ResponseMetaData } from '@/types/service';

// localhost:3003
const http = createHttp(import.meta.env.VITE_BV_URL);

export const getBenefitVerifications = async (
	page = 1,
	limit = 15,
	columnsFilters = '[]',
	search = '',
	status = '',
	globalFilters = '[]',
	sort = '',
): Promise<{
	items: {
		bvData: BenefitVerification[];
	};
	meta: ResponseMetaData;
}> => {
	const [bvResponse] = await Promise.all([
		http.get<BVResponse>('/bvs/db', {
			params: {
				page,
				limit,
				columnsFilters,
				search,
				status,
				globalFilters,
				sort,
			},
		})
	]);
	return {
		items: {
			bvData: bvResponse.data.data.items,
		},
		meta: bvResponse.data.data.metaData,
	};
};

export const getBenefitVerificationsProducts = async (
	page = 1,
	limit = 15,
	status = '',
	globalFilters = '[]',
): Promise<{
	items: {
		productData: string[];
	};
}> => {
	const productResponse = await http.get('/bvs/db/bv-products', {
		params: {
			page,
			limit,
			columnsFilters: '[]',
			search: '',
			status,
			globalFilters,
		},
	});

	return {
		items: {
			productData: productResponse.data.data.items,
		},
	};
};

export const addUpdateNewBenefitVerification = async (
	data: BenefitVerificationPayload,
): Promise<BVFormData> => {
	if (!data.id) {
		const response = await http.post<BVFormData>('/bvs', data);
		return response.data;
	}
	const id = data.id;
	delete data.id;
	// Id is present, so we are updating an existing BV
	const response = await http.patch<BVFormData>(`/bvs/${id}`, data);
	return response.data;
};

export const getSingleBV = async (id: string): Promise<BVRecord> => {
	const response = await http.get(`/bvs/db/${id}`);
	return response.data.data;
};

export const getIVRFile = async (priorAuthId: string): Promise<IVRFileData> => {
	const response = await http.get<{ data: IVRFileData }>(
		`/bvs/ivr/view/${priorAuthId}`,
	);
	return response.data.data;
};

export const getPRDocument = async (
	benefitVerificationId: string,
): Promise<PRDocumentData> => {
	const response = await http.get<{ data: PRDocumentData }>(
		`/bvs/pverify/pdf-timeline/${benefitVerificationId}`,
	);
	return response.data.data;
};

export interface VoidBVPayload {
	ta_bv_status: string;
}

export const voidBenefitVerification = async (
	benefitVerificationId: string,
): Promise<void> => {
	const payload: VoidBVPayload = {
		ta_bv_status: '953870026', // set to void status
	};
	await http.patch(`/bvs/${benefitVerificationId}`, payload);
};

export const updateBenefitVerification = async (payload: {
	benefitVerificationId: string;
	payload: Partial<BenefitVerificationPayload>;
}): Promise<void> => {
	await http.patch(`/bvs/${payload.benefitVerificationId}`, payload.payload);
};

export const updateBVProduct = async (payload: {
	priorAuthId: string;
	payload: UpdateBVProductPayload;
}): Promise<void> => {
	await http.patch(`/bvs/product/${payload.priorAuthId}`, payload.payload);
};

export const getRecentBVs  = async (page: number, limit: number, globalFilters: string): Promise<{
	items: BenefitVerification[];
	meta: ResponseMetaData;
}> => {
	const result = await http.get(`/bvs/db/recent`, {
		params: {
			page,
			limit,
			globalFilters
		}
	});

	return {
		items: result.data.data.items,
		meta: result.data.data.metaData,
	};
};

export const getRecentBvCount  = async (page: number, limit: number, globalFilters: string, dateRangeFilter: string): Promise<{
		createdLast24Hours: number;
		pendingCount: number;
		approvedCount: number;
		deniedCount: number;
}> => {
	const result = await http.get(`/bvs/db/counts`, {
		params: {
			page,
			limit,
			globalFilters,
			dateRangeFilter
		}
	});

	return result.data.data;
};